import { useState, useCallback } from 'react'
import { X, RotateCcw } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@/components/ui/Button'
import type { FacilityType, EvaluationGrade } from '@/types/common'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import { cn } from '@/lib/utils'

/** 평가 등급 목록 */
const EVALUATION_GRADES: EvaluationGrade[] = ['A', 'B', 'C', 'D', 'E']

/** 시설 유형 목록 */
const FACILITY_TYPES: FacilityType[] = [
  'NURSING_HOME',
  'NURSING_HOME_SMALL',
  'NURSING_HOSPITAL',
  'DAY_CARE',
  'HOME_VISIT_CARE',
  'HOME_VISIT_BATH',
  'HOME_VISIT_NURSING',
  'HOME_CARE_CENTER',
  'DEMENTIA_CARE',
  'WELFARE_EQUIPMENT',
  'SHORT_STAY',
]

export interface FilterState {
  region?: string
  types: FacilityType[]
  grades: EvaluationGrade[]
  hasDementiaCare?: boolean
  hasVacancy?: boolean
  hasPrograms?: boolean
}

interface FacilityFilterProps {
  isOpen: boolean
  onClose: () => void
  currentFilter: FilterState
  onApply: (filter: FilterState) => void
}

export default function FacilityFilter({
  isOpen,
  onClose,
  currentFilter,
  onApply,
}: FacilityFilterProps) {
  const [localFilter, setLocalFilter] = useState<FilterState>(currentFilter)

  const handleOpen = useCallback(() => {
    setLocalFilter(currentFilter)
  }, [currentFilter])

  const handleTypeToggle = (type: FacilityType) => {
    setLocalFilter((prev) => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter((t) => t !== type)
        : [...prev.types, type],
    }))
  }

  const handleGradeToggle = (grade: EvaluationGrade) => {
    setLocalFilter((prev) => ({
      ...prev,
      grades: prev.grades.includes(grade)
        ? prev.grades.filter((g) => g !== grade)
        : [...prev.grades, grade],
    }))
  }

  const handleToggle = (key: 'hasDementiaCare' | 'hasVacancy' | 'hasPrograms') => {
    setLocalFilter((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleReset = () => {
    setLocalFilter({ types: [], grades: [] })
  }

  const handleApply = () => {
    onApply(localFilter)
    onClose()
  }

  const activeFilterCount =
    localFilter.types.length +
    localFilter.grades.length +
    (localFilter.hasDementiaCare ? 1 : 0) +
    (localFilter.hasVacancy ? 1 : 0) +
    (localFilter.hasPrograms ? 1 : 0)

  return (
    <AnimatePresence onExitComplete={() => {}}>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onAnimationComplete={(definition) => {
              if (definition === 'animate') handleOpen()
            }}
            className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)]"
            role="dialog"
            aria-label="검색 필터"
            aria-modal="true"
          >
            <div className="sticky top-0 flex items-center justify-center bg-white pt-3 pb-2">
              <div className="h-1 w-10 rounded-full bg-cb-gray-300" />
            </div>

            <div className="flex items-center justify-between px-5 pb-4">
              <h2 className="text-h3 font-bold text-cb-gray-900">필터</h2>
              <button
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-cb-gray-100"
                aria-label="필터 닫기"
              >
                <X size={22} className="text-cb-gray-600" />
              </button>
            </div>

            <div className="px-5 flex flex-col gap-6">
              {/* 시설 유형 */}
              <section>
                <h3 className="text-body font-semibold text-cb-gray-800 mb-3">시설 유형</h3>
                <div className="flex flex-wrap gap-2">
                  {FACILITY_TYPES.map((type) => (
                    <button
                      key={type}
                      onClick={() => handleTypeToggle(type)}
                      className={cn(
                        'rounded-full border px-3.5 py-2 text-body-sm transition-colors',
                        localFilter.types.includes(type)
                          ? 'border-cb-primary bg-cb-primary-light text-cb-primary font-medium'
                          : 'border-cb-gray-300 text-cb-gray-600 hover:border-cb-gray-400'
                      )}
                    >
                      {FACILITY_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
              </section>

              {/* 평가 등급 */}
              <section>
                <h3 className="text-body font-semibold text-cb-gray-800 mb-3">평가 등급</h3>
                <div className="flex gap-2.5">
                  {EVALUATION_GRADES.map((grade) => (
                    <button
                      key={grade}
                      onClick={() => handleGradeToggle(grade)}
                      className={cn(
                        'flex h-11 w-11 items-center justify-center rounded-full border-2 text-body font-bold transition-colors',
                        localFilter.grades.includes(grade)
                          ? 'border-cb-primary bg-cb-primary-light text-cb-primary'
                          : 'border-cb-gray-200 text-cb-gray-500 hover:border-cb-gray-300'
                      )}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </section>

              {/* 추가 조건 */}
              <section>
                <h3 className="text-body font-semibold text-cb-gray-800 mb-3">추가 조건</h3>
                <div className="flex flex-col gap-3">
                  <ToggleRow
                    label="치매전담 운영"
                    description="치매전담실을 운영하는 시설"
                    checked={!!localFilter.hasDementiaCare}
                    onChange={() => handleToggle('hasDementiaCare')}
                  />
                  <ToggleRow
                    label="여유 정원 있음"
                    description="바로 입소 가능한 시설"
                    checked={!!localFilter.hasVacancy}
                    onChange={() => handleToggle('hasVacancy')}
                  />
                  <ToggleRow
                    label="프로그램 운영"
                    description="인지/신체/여가 프로그램 운영 시설"
                    checked={!!localFilter.hasPrograms}
                    onChange={() => handleToggle('hasPrograms')}
                  />
                </div>
              </section>
            </div>

            {/* 하단 버튼 */}
            <div className="sticky bottom-0 mt-6 flex gap-3 border-t border-cb-gray-200 bg-white px-5 py-4">
              <Button
                variant="ghost"
                size="lg"
                onClick={handleReset}
                className="flex-shrink-0 whitespace-nowrap px-5 flex items-center gap-1"
              >
                <RotateCcw size={16} />
                초기화
              </Button>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleApply}
              >
                {activeFilterCount > 0 ? `필터 적용 (${activeFilterCount})` : '필터 적용'}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/** 토글 행 컴포넌트 */
function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <button
      onClick={onChange}
      className={cn(
        'flex items-center justify-between rounded-xl border-2 p-3.5 transition-all',
        checked
          ? 'border-cb-primary bg-cb-primary-light'
          : 'border-cb-gray-200 bg-white hover:border-cb-gray-300'
      )}
    >
      <div className="text-left">
        <p className={cn('text-body-sm font-semibold', checked ? 'text-cb-primary' : 'text-cb-gray-900')}>
          {label}
        </p>
        <p className="text-caption text-cb-gray-500 mt-0.5">{description}</p>
      </div>
      <div className={cn(
        'relative h-6 w-11 rounded-full transition-colors',
        checked ? 'bg-cb-primary' : 'bg-cb-gray-300'
      )}>
        <div className={cn(
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )} />
      </div>
    </button>
  )
}
